/**
 * The guard on client-driven enquiry status transitions.
 *
 * ---- Why the client drives any of these at all ----
 *
 * It should not. A workflow transition caused by a server-side event belongs to the server, and
 * `docs/bosun-frappe-asks.md`'s `D13` asks the Frappe team for exactly that: `Quotes Received`
 * when a `Supplier Quotation` is created, `Comparison Done` on `save_comparison`, `Quotation Sent`
 * on `send_quotation`. Only the last of those exists today. Measured 2026-08-08:
 * `ENQ-2026-00013` carried a fully-priced draft quotation (`QTN-2026-00004`, AED 210.45, built
 * 20:54) while its status still read `RFQ Sent` from 14:39 — six hours earlier. To every user
 * looking at the Requirements board, that enquiry was still out at RFQ.
 *
 * So `Comparison Done` is written from here as a STOPGAP, alongside the `RFQ Sent` write that has
 * lived in `useEnquiryMutations.ts` since long before this file. Both go through
 * `enquiry.update_enquiry`, so they persist server-side and read the same for every user — this
 * is not a per-client display fix, which is the one thing it could not be.
 *
 * Delete both the day D13 ships, and this module with them.
 *
 * ---- What this guard is actually for ----
 *
 * A client-driven transition can run more than once, and out of order. `save_comparison` is not a
 * one-shot: a purchaser can reopen a comparison and re-save it after the quotation has gone to the
 * customer, or after the enquiry was won. Writing `Comparison Done` at that moment would drag a
 * decided enquiry back into the middle of the board — and because the write persists, it would do
 * so for everyone, which is far worse than the stale status this fix exists to remove.
 *
 * Hence: only ever forwards, never sideways, never twice.
 *
 * ---- The ordering is `ENQUIRY_STATUSES` itself ----
 *
 * That array is in FLOW order, not alphabetical and not merely the board's column order:
 * `Draft → Open → RFQ Sent → Quotes Received → Comparison Done → Quotation Sent → Won → Rejected
 * → Archived`. Reading it as the flow keeps one definition of "later" rather than a second table
 * that could drift from the first. The coupling is real and load-bearing, so `statusFlow.test.ts`
 * pins the two positions this depends on — reordering that array changes which transitions are
 * legal, and a test will say so.
 *
 * `Won`/`Rejected`/`Archived` all sit after `Quotation Sent`, so they are terminal as far as this
 * function is concerned. That is the behaviour we want; ranking them relative to each other is
 * meaningless and nothing asks it to.
 */

import { ENQUIRY_STATUSES, type EnquiryStatus } from './types'

/**
 * Whether `target` is strictly later in the flow than `current` — i.e. whether writing it would
 * move the enquiry FORWARD.
 *
 * Returns `false` when the enquiry is already at `target`, so a re-save is a no-op rather than a
 * redundant write, and `false` for anything at or past `target`.
 */
export function shouldAdvanceTo(current: EnquiryStatus, target: EnquiryStatus): boolean {
  return ENQUIRY_STATUSES.indexOf(current) < ENQUIRY_STATUSES.indexOf(target)
}
